import { FC, useState, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Paper,
  Alert,
  TextField,
  Autocomplete,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { BranchData } from '@/utilities/types/table'

const infoBoxStyle = {
  bgcolor: '#F3F4F6',
  border: '1px solid',
  borderColor: 'rgb(226, 232, 240)',
  borderRadius: '4px',
  p: 2,
  width: '100%',
}

interface ModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: BranchData[], areaName: string) => void // ⬅ include areaName if you want to capture it
  formData: BranchData[]
  setFormData: React.Dispatch<React.SetStateAction<BranchData[]>>
  branches: BranchData[]
  existingBranches?: BranchData[]
  areaName: string
  areaCode: string
  isLoading?: boolean
  error?: string | null
}

export const AssignProvinceModal: FC<ModalProps> = ({
  open,
  onClose,
  onSubmit,
  formData,
  setFormData,
  branches = [],
  existingBranches = [],
  areaName,
  areaCode,
  isLoading = false,
  error = null,
}) => {
  const [selectedBranch, setSelectedBranch] = useState<BranchData | null>(null)
  const [editableAreaName, setEditableAreaName] = useState(areaName) 
  
  const filteredBranchOptions = useMemo(() => {
    const branchMap = new Map<number, BranchData>()
    branches.forEach((b) => branchMap.set(b.id, b))
    existingBranches?.forEach((b) => {
      if (!branchMap.has(b.id)) branchMap.set(b.id, b)
    })
    const existingIds = new Set(formData.map((b) => b.id))
    return Array.from(branchMap.values())
      .filter((b) => !existingIds.has(b.id))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [branches, existingBranches, formData])

  const handleAddBranch = (branch: BranchData | null) => {
    if (branch && !formData.some((b) => b.id === branch.id)) {
      setFormData((prev) => [...prev, branch])
    }
    setSelectedBranch(null)
  }

  const handleRemove = (branchId: number) => {
    setFormData((prev) => prev.filter((b) => b.id !== branchId))
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Assign Branches</Typography>
          <IconButton onClick={onClose} disabled={isLoading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* ✅ Editable Area Name */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Area
          </Typography>
          <TextField
            fullWidth
            value={editableAreaName}
            onChange={(e) => setEditableAreaName(e.target.value)}
            variant="outlined"
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Area Code
          </Typography>
          <Paper variant="outlined" sx={infoBoxStyle}>
            <Typography>{areaCode}</Typography>
          </Paper>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Assigned Branches
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, minHeight: 80 }}>
            {!formData?.length ? (
              <Typography color="text.secondary">
                No Branches assigned
              </Typography>
            ) : (
              [...formData]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((branch) => (
                  <Paper
                    key={branch.id}
                    variant="outlined"
                    sx={{ p: 2, mb: 1 }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography>
                        {branch.name} - {branch.dealerName}
                      </Typography>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleRemove(branch.id)}
                      >
                        Remove
                      </Button>
                    </Box>
                  </Paper>
                ))
            )}
          </Paper>
        </Box>

        <Autocomplete
          options={filteredBranchOptions}
          value={selectedBranch}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(e, newValue) => handleAddBranch(newValue)}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.name} - {option.dealerName}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Add Branch"
              variant="outlined"
              fullWidth
              inputProps={{
                ...params.inputProps,
                autoComplete: 'new-password',
              }}
            />
          )}
          PopperProps={{
            modifiers: [
              { name: 'flip', enabled: false },
              {
                name: 'preventOverflow',
                enabled: true,
                options: { altAxis: true, tether: false },
              },
            ],
            placement: 'bottom-start',
          }}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          // ✅ include editableAreaName in submit
          onClick={() => onSubmit(formData, editableAreaName)}
          disabled={isLoading}
        >
          {isLoading ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
